import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { GlassCard, PageHeader } from "../components/shared";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { 
    Search, MapPin, Calendar, Navigation 
} from "lucide-react";

export default function Tracking() {
    const [googleLoaded, setGoogleLoaded] = useState(false);
    const [activeUsers, setActiveUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [historyDate, setHistoryDate] = useState(new Date().toISOString().split("T")[0]);
    const [routeCoords, setRouteCoords] = useState([]);
    const [distanceTravelled, setDistanceTravelled] = useState("0.00");
    const [loading, setLoading] = useState(false);

    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const markersRef = useRef([]);
    const polylineRef = useRef(null);

    // Initialize Google Maps SDK Loader
    useEffect(() => {
        setOptions({
            apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
            version: "weekly"
        });

        Promise.all([
            importLibrary("maps"),
            importLibrary("geometry")
        ]).then(() => {
            setGoogleLoaded(true);
        }).catch((e) => {
            console.error("Failed to load Google Maps SDK", e);
            toast.error("Google Maps SDK failed to load. Check API key.");
        });
    }, []);

    // Fetch active users list
    const fetchActiveUsers = async () => {
        try {
            const res = await api.get("/attendance/active-locations");
            setActiveUsers(res.data.active_users || []);
        } catch (e) {
            toast.error("Failed to load active locations");
        }
    };

    useEffect(() => {
        fetchActiveUsers();
        const poll = setInterval(fetchActiveUsers, 30000); // 30s polling
        return () => clearInterval(poll);
    }, []);

    // Initialize Map
    useEffect(() => {
        if (googleLoaded && !mapRef.current && mapContainerRef.current) {
            const mapStyles = [
                {
                    "featureType": "poi",
                    "elementType": "labels",
                    "stylers": [{ "visibility": "off" }]
                },
                {
                    "featureType": "transit",
                    "elementType": "labels.icon",
                    "stylers": [{ "visibility": "off" }]
                }
            ];

            mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
                center: { lat: 20.5937, lng: 78.9629 },
                zoom: 5,
                disableDefaultUI: true,
                styles: mapStyles
            });
        }
    }, [googleLoaded]);

    // Load path and render markers/polyline
    const fetchLocationHistory = async (userId, dateStr) => {
        if (!googleLoaded || !mapRef.current) return;
        setLoading(true);
        
        try {
            const res = await api.get(`/attendance/location-history/${userId}/${dateStr}`);
            const coords = res.data.coordinates || [];
            setRouteCoords(coords);

            // Define custom overlay helper for HTML rendering on Google Maps
            const CustomOverlay = class extends window.google.maps.OverlayView {
                constructor(latlng, html, map) {
                    super();
                    this.latlng = latlng;
                    this.html = html;
                    this.setMap(map);
                }
                onAdd() {
                    this.div = document.createElement("div");
                    this.div.style.position = "absolute";
                    this.div.innerHTML = this.html;
                    const panes = this.getPanes();
                    panes.overlayMouseTarget.appendChild(this.div);
                }
                draw() {
                    const overlayProjection = this.getProjection();
                    const position = overlayProjection.fromLatLngToDivPixel(this.latlng);
                    if (position && this.div) {
                        this.div.style.left = position.x + "px";
                        this.div.style.top = position.y + "px";
                    }
                }
                onRemove() {
                    if (this.div) {
                        this.div.parentNode.removeChild(this.div);
                        this.div = null;
                    }
                }
            };

            // Clear old map layers
            markersRef.current.forEach(m => m.setMap(null));
            markersRef.current = [];
            if (polylineRef.current) {
                polylineRef.current.setMap(null);
                polylineRef.current = null;
            }

            if (coords.length === 0) {
                const activeUser = activeUsers.find(u => u.user_id === userId);
                if (activeUser && activeUser.latest_location) {
                    const latlng = new window.google.maps.LatLng(activeUser.latest_location.lat, activeUser.latest_location.lng);
                    mapRef.current.setCenter(latlng);
                    mapRef.current.setZoom(15);
                    
                    const marker = new CustomOverlay(
                        latlng,
                        '<div class="relative w-8 h-8 flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2"><div class="absolute w-6 h-6 bg-teal-500 rounded-full animate-ping opacity-75"></div><div class="relative w-6 h-6 rounded-full bg-teal-600 border-2 border-white flex items-center justify-center text-white text-[9px] font-bold shadow-md">LOC</div></div>',
                        mapRef.current
                    );
                    markersRef.current.push(marker);
                    setDistanceTravelled("0.00");
                } else {
                    toast.info("No location coordinates logged for this day.");
                    setDistanceTravelled("0.00");
                }
                return;
            }

            const pathCoords = coords.map(c => new window.google.maps.LatLng(c.lat, c.lng));

            // Draw route line
            polylineRef.current = new window.google.maps.Polyline({
                path: pathCoords,
                geodesic: true,
                strokeColor: '#0d9488',
                strokeOpacity: 0.8,
                strokeWeight: 4,
                map: mapRef.current
            });

            // Calculate distance using Google geometry SDK
            let dist = 0;
            for (let i = 0; i < coords.length - 1; i++) {
                const p1 = pathCoords[i];
                const p2 = pathCoords[i+1];
                dist += window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
            }
            setDistanceTravelled((dist / 1000).toFixed(2));

            // Start Position marker
            const startMarker = new CustomOverlay(
                pathCoords[0],
                '<div class="w-6 h-6 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center text-white text-[9px] font-bold shadow-md transform -translate-x-1/2 -translate-y-1/2">IN</div>',
                mapRef.current
            );
            markersRef.current.push(startMarker);

            // Waypoints
            for (let i = 1; i < pathCoords.length - 1; i++) {
                const dotMarker = new CustomOverlay(
                    pathCoords[i],
                    '<div class="w-3 h-3 rounded-full bg-teal-500/80 border border-white shadow-sm transform -translate-x-1/2 -translate-y-1/2"></div>',
                    mapRef.current
                );
                markersRef.current.push(dotMarker);
            }

            // Latest Location marker
            const latestMarker = new CustomOverlay(
                pathCoords[pathCoords.length - 1],
                '<div class="relative w-8 h-8 flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2"><div class="absolute w-6 h-6 bg-red-500 rounded-full animate-ping opacity-75"></div><div class="relative w-6 h-6 rounded-full bg-red-600 border-2 border-white flex items-center justify-center text-white text-[9px] font-bold shadow-md">OUT</div></div>',
                mapRef.current
            );
            markersRef.current.push(latestMarker);

            // Fit map to zoom path
            const bounds = new window.google.maps.LatLngBounds();
            pathCoords.forEach(p => bounds.extend(p));
            mapRef.current.fitBounds(bounds);

        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedUser) {
            fetchLocationHistory(selectedUser.user_id, historyDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser, historyDate, googleLoaded]);

    const filteredUsers = activeUsers.filter(u => 
        u.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="h-[calc(100vh-140px)] flex flex-col">
            <PageHeader 
                title="Live GPS Tracking" 
                subtitle="Track real-time GPS locations and route histories of active employees & interns." 
            />

            <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-6 min-h-0">
                {/* Active List */}
                <div className="lg:col-span-1 flex flex-col gap-4 min-h-0">
                    <GlassCard className="p-4 flex flex-col min-h-0 h-full">
                        <div className="relative mb-3">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input 
                                placeholder="Search active users..." 
                                className="pl-9" 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 px-1">Punched In Today ({filteredUsers.length})</h4>
                            
                            {filteredUsers.length === 0 ? (
                                <div className="text-center py-8 text-xs text-muted-foreground">
                                    No active employees tracking right now.
                                </div>
                            ) : (
                                filteredUsers.map((u) => {
                                    const isSelected = selectedUser?.user_id === u.user_id;
                                    const initials = u.name.split(" ").map(n => n[0]).join("").toUpperCase();
                                    return (
                                        <div 
                                            key={u.user_id}
                                            onClick={() => setSelectedUser(u)}
                                            className={`p-3 rounded-xl border cursor-pointer transition flex items-center gap-3 ${
                                                isSelected 
                                                    ? 'bg-teal-50 border-teal-200 dark:bg-teal-950/20 dark:border-teal-900/30' 
                                                    : 'bg-white/40 border-black/5 hover:bg-white/70 dark:bg-slate-900/30 dark:border-white/5 dark:hover:bg-slate-900/50'
                                            }`}
                                        >
                                            {u.avatar ? (
                                                <img src={u.avatar} alt={u.name} className="w-9 h-9 rounded-full object-cover shadow-sm" />
                                            ) : (
                                                <div className="w-9 h-9 rounded-full bg-slate-900 text-white grid place-items-center text-[10px] font-bold uppercase">
                                                    {initials}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-semibold truncate text-slate-800 dark:text-slate-100">{u.name}</div>
                                                <div className="text-[11px] text-muted-foreground truncate">{u.designation}</div>
                                            </div>
                                            {u.latest_location ? (
                                                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                            ) : (
                                                <div className="w-2 h-2 rounded-full bg-slate-300" />
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {selectedUser && (
                            <div className="border-t pt-4 mt-4 space-y-4">
                                <div>
                                    <Label className="text-xs font-bold text-muted-foreground">Historical Date</Label>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <Calendar className="w-4 h-4 text-muted-foreground" />
                                        <Input 
                                            type="date" 
                                            value={historyDate}
                                            onChange={(e) => setHistoryDate(e.target.value)}
                                            className="text-xs h-9"
                                        />
                                    </div>
                                </div>

                                <div className="p-3.5 rounded-xl bg-teal-50/50 dark:bg-teal-950/10 border border-teal-100/50 dark:border-teal-900/20 space-y-2">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-muted-foreground">Total Pings:</span>
                                        <span className="font-semibold">{routeCoords.length} points</span>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-muted-foreground">Distance:</span>
                                        <span className="font-semibold text-teal-600 dark:text-teal-400">{distanceTravelled} km</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </GlassCard>
                </div>

                {/* Map Display */}
                <div className="lg:col-span-3 h-full min-h-[400px]">
                    <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-lg border bg-white/40 dark:bg-slate-900/10">
                        {loading && (
                            <div className="absolute inset-0 bg-white/55 dark:bg-black/55 z-[1000] flex items-center justify-center">
                                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                    <Navigation className="w-4 h-4 animate-spin text-teal-600" /> Plotting route...
                                </div>
                            </div>
                        )}
                        {!selectedUser && (
                            <div className="absolute inset-0 bg-slate-50/70 dark:bg-slate-950/70 z-[999] flex flex-col items-center justify-center p-6 text-center">
                                <MapPin className="w-12 h-12 text-teal-500 mb-3 animate-bounce" />
                                <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Live GPS Monitor</h3>
                                <p className="text-xs text-muted-foreground max-w-sm mt-1">Select an active user from the left panel to display their real-time location track and route history.</p>
                            </div>
                        )}
                        <div ref={mapContainerRef} className="w-full h-full z-10" />
                    </div>
                </div>
            </div>
        </div>
    );
}
